import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listingsApi, type ListingListParams } from "@/api/buyers";
import { listingKeys } from "@/hooks/buyers/buyerKeys";
import { AttentionTile } from "../../indexTiles/AttentionTile";
import { IndexTileDeck, Measure, MeasureTile } from "../../indexTiles/MeasureTile";
import { StageDistributionTile } from "../../indexTiles/StageDistributionTile";
import { VolumeTile } from "../../indexTiles/VolumeTile";
import { ratePct, type StageSegment, type StageTone } from "../../indexTiles/tileBits";
import { QueryErrorState } from "../../queryStates";
import { listHref } from "../reportLinks";

/** Mirror of the list page's window for the "no buyer presented" pill. */
const NO_BUYER_DAYS = 30;

/**
 * Labels and colours for the engagement ladder — the list page's map, copied.
 * The rungs and their ORDER come from the server; an unrecognised key still
 * renders (humanised, in "working" tone) rather than vanishing.
 */
const ENGAGEMENT_META: Record<string, { label: string; tone: StageTone }> = {
  untouched: { label: "Untouched", tone: "early" },
  "in-play": { label: "In play", tone: "advanced" },
  "under-offer": { label: "Under offer", tone: "committed" },
  won: { label: "Won", tone: "won" },
  "closed-out": { label: "Closed out", tone: "ended" },
};

function humanise(key: string): string {
  const words = key.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The four listing tiles, over the list page's stats read.
 *
 * <p>A listing is a SHARED catalog record with no team — the stats endpoint
 * takes no team parameter and none is sent — so this tab reads the same
 * regardless of the page's team filter, and says so.</p>
 */
export function ListingsReportTab() {
  const navigate = useNavigate();
  const params = useMemo<ListingListParams>(() => ({ page: 1 }), []);
  const stats = useQuery({
    queryKey: listingKeys.stats(params),
    queryFn: () => listingsApi.stats(params),
  });

  const stageSegments = useMemo<StageSegment[]>(
    () =>
      Object.entries(stats.data?.byEngagement ?? {}).map(([key, count]) => ({
        key,
        label: ENGAGEMENT_META[key]?.label ?? humanise(key),
        count,
        tone: ENGAGEMENT_META[key]?.tone ?? "working",
      })),
    [stats.data],
  );

  if (stats.isError) {
    return (
      <QueryErrorState
        what="listing figures"
        onRetry={() => void stats.refetch()}
        retrying={stats.isFetching}
      />
    );
  }

  const share = ratePct(stats.data?.withBuyerPresented ?? 0, stats.data?.total ?? 0);

  return (
    <>
      <p className="text-caption text-gray-mid mb-space-3">
        Listings are a shared catalog and are not scoped to a team.
      </p>
      <IndexTileDeck>
        <StageDistributionTile
          title="Engagement"
          segments={stageSegments}
          onSelect={(key) => navigate(listHref("/listings", { phase: key }))}
          loading={stats.isLoading}
        />

        <AttentionTile
          items={[
            {
              key: "no-agent-email",
              label: "No agent email",
              count: stats.data?.noAgentEmail ?? 0,
            },
            {
              key: "no-price",
              label: "No price stated",
              count: stats.data?.noAskingPrice ?? 0,
            },
            {
              key: "no-buyer",
              label: `No buyer presented in ${NO_BUYER_DAYS} days`,
              count: stats.data?.noBuyerPresentedRecently ?? 0,
            },
          ]}
          onSelect={(key) => navigate(listHref("/listings", { card: key }))}
          loading={stats.isLoading}
        />

        <VolumeTile
          title="Listings added"
          points={stats.data?.createdByMonth ?? []}
          loading={stats.isLoading}
        />

        <MeasureTile title="Reach">
          <Measure
            label="With a buyer presented"
            value={share === null ? "—" : `${share}%`}
            detail={`${stats.data?.withBuyerPresented ?? 0} of ${stats.data?.total ?? 0} listings`}
          />
        </MeasureTile>
      </IndexTileDeck>
    </>
  );
}
